import { useState, useEffect, type FormEvent } from 'react';
import { useAuth } from '../lib/auth';
import { api, ApiRequestError } from '../lib/api';

interface UserProfile {
  id: string;
  email: string;
  display_name: string;
  role: string;
  tier: { id: string; name: string } | null;
  credit_balance: number;
  created_at: string;
  quota_usage?: {
    daily_generations_used: number;
    daily_generations_limit: number;
    concurrent_jobs_active: number;
    concurrent_jobs_limit: number;
    storage_used_mb: number;
    storage_limit_mb: number;
  };
}

export function Profile() {
  const { refreshUser } = useAuth();
  const [profile, setProfile] = useState<UserProfile | null>(null);
  const [displayName, setDisplayName] = useState('');
  const [editing, setEditing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  useEffect(() => {
    api
      .get<UserProfile>('/users/me')
      .then((res) => {
        setProfile(res.data);
        setDisplayName(res.data.display_name);
      })
      .catch(() => setError('Failed to load profile'));
  }, []);

  const handleSave = async (e: FormEvent) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setSaving(true);
    try {
      const res = await api.patch<UserProfile>('/users/me', { display_name: displayName });
      setProfile(res.data);
      setEditing(false);
      setSuccess('Profile updated');
      refreshUser();
    } catch (err) {
      if (err instanceof ApiRequestError) {
        setError(err.error.message);
      } else {
        setError('Failed to update profile');
      }
    } finally {
      setSaving(false);
    }
  };

  if (!profile) {
    return <div className="text-center py-12 text-gray-500">Loading profile...</div>;
  }

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Profile</h1>

      {error && (
        <div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-700">{error}</div>
      )}
      {success && (
        <div className="p-3 bg-green-50 border border-green-200 rounded-md text-sm text-green-700">{success}</div>
      )}

      {/* Profile Info */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6 space-y-4">
        <div className="flex items-center justify-between">
          <h2 className="text-lg font-semibold text-gray-900">Account Information</h2>
          {!editing && (
            <button
              onClick={() => setEditing(true)}
              className="text-sm text-blue-600 hover:text-blue-700 min-h-[44px] px-3"
            >
              Edit
            </button>
          )}
        </div>

        {editing ? (
          <form onSubmit={handleSave} className="space-y-4">
            <div>
              <label htmlFor="displayName" className="block text-sm font-medium text-gray-700 mb-1">
                Display name
              </label>
              <input
                id="displayName"
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                required
                minLength={1}
                maxLength={100}
                className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
              />
            </div>
            <div className="flex gap-2">
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-blue-600 text-white rounded-md text-sm hover:bg-blue-700 disabled:opacity-50 min-h-[44px]"
              >
                {saving ? 'Saving...' : 'Save'}
              </button>
              <button
                type="button"
                onClick={() => {
                  setEditing(false);
                  setDisplayName(profile.display_name);
                }}
                className="px-4 py-2 border border-gray-300 rounded-md text-sm hover:bg-gray-50 min-h-[44px]"
              >
                Cancel
              </button>
            </div>
          </form>
        ) : (
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <dt className="text-sm text-gray-500">Display name</dt>
              <dd className="text-sm font-medium text-gray-900">{profile.display_name}</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-500">Email</dt>
              <dd className="text-sm font-medium text-gray-900">{profile.email}</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-500">Plan</dt>
              <dd className="text-sm font-medium text-gray-900 capitalize">{profile.tier?.name ?? 'N/A'}</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-500">Role</dt>
              <dd className="text-sm font-medium text-gray-900 capitalize">{profile.role}</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-500">Credit balance</dt>
              <dd className="text-sm font-medium text-gray-900">{profile.credit_balance}</dd>
            </div>
            <div>
              <dt className="text-sm text-gray-500">Member since</dt>
              <dd className="text-sm font-medium text-gray-900">
                {new Date(profile.created_at).toLocaleDateString()}
              </dd>
            </div>
          </dl>
        )}
      </div>

      {/* Quota Usage */}
      {profile.quota_usage && (
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <h2 className="text-lg font-semibold text-gray-900 mb-4">Usage</h2>
          <div className="space-y-4">
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Daily generations</span>
                <span className="text-gray-900">
                  {profile.quota_usage.daily_generations_used} / {profile.quota_usage.daily_generations_limit}
                </span>
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-full">
                <div
                  className="h-2 bg-blue-500 rounded-full"
                  style={{
                    width: `${Math.min(100, (profile.quota_usage.daily_generations_used / (profile.quota_usage.daily_generations_limit || 1)) * 100)}%`,
                  }}
                />
              </div>
            </div>
            <div>
              <div className="flex justify-between text-sm mb-1">
                <span className="text-gray-600">Storage</span>
                <span className="text-gray-900">
                  {profile.quota_usage.storage_used_mb.toFixed(1)} MB /{' '}
                  {profile.quota_usage.storage_limit_mb} MB
                </span>
              </div>
              <div className="w-full h-2 bg-gray-200 rounded-full">
                <div
                  className="h-2 bg-blue-500 rounded-full"
                  style={{
                    width: `${Math.min(100, (profile.quota_usage.storage_used_mb / (profile.quota_usage.storage_limit_mb || 1)) * 100)}%`,
                  }}
                />
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
